import { ToolbarGlassSurface } from "#toolbar-glass-surface";
import type { ButtonProps } from "@-/dev.improve.haniverse.components.ui.button";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { cn } from "@-/com.npmjs.cn";
import {
  Platform,
  Pressable,
  ScrollView,
  View,
  type ScrollViewProps,
} from "@-/com.npmjs.react-native";
import {
  HorizontalScrollEdgeFadeMask,
  useHorizontalScrollEdges,
} from "@-/dev.improve.haniverse.components.ui.scroll-edge-fade";
import { useToolbarGridMetrics } from "#use-toolbar-grid-metrics";

export type ToolbarViewButtonProps = Omit<
  ButtonProps,
  "children" | "className" | "size" | "style" | "variant"
> & {
  /** Reduces the pill height by 8px for secondary filter rows. */
  compact?: boolean;
  label: string;
  /**
   * When false, disable the native glass press animation. Useful for dropdown triggers
   * that already read as selected and should not look like a second toggle.
   */
  pressFeedback?: boolean;
  /** Highlights the pill as the active selection (stronger glass tint + emphasized text). */
  selected?: boolean;
  /** Optional second line under the label (e.g. a count), in smaller muted text. */
  sublabel?: string;
};

const MORE_TRIGGER_BALANCED_GUTTER_CLASS_NAME = "px-0";

/**
 * Shared lower layer for page-level text-pill toolbars. Native app pages reserve a right-most
 * tile-sized lane for the drawer. Pages with a floating More trigger opt into a
 * matching left-most lane with `hasLeadingActions`, keeping the scroll viewport between the two
 * upper-layer circles without making pages that have no menu waste that space. Web has no
 * app-shell profile trigger (the header owns it) so it keeps the trailing edge, but it DOES render
 * the More lane and reserves it the same way. Menu-bearing rows use equal internal edge padding;
 * the menu opens above the pills without moving them or changing their scroll position. A lane
 * whose shell control is hidden (`ToolbarDrawerButtonHiddenContext`,
 * `ToolbarLeadingSlotHiddenContext`) is the row's: the viewport reaches the window edge there.
 */
export function ToolbarPillRow({
  children,
  className,
  contentContainerClassName,
  contentContainerStyle,
  hasLeadingActions = false,
  leadingControlWidth = 0,
  onContentSizeChange,
  onLayout,
  onScroll,
  scrollEventThrottle = 16,
  style,
  ...props
}: Omit<ScrollViewProps, "horizontal"> &
  React.RefAttributes<ScrollView> & {
    hasLeadingActions?: boolean;
    /**
     * Width of the control before More, excluding the lane's 4px gap. Back and undo controls
     * have their own widths; treating them as responsive circles leaves extra space before pills.
     */
    leadingControlWidth?: number;
  }) {
  const {
    diameter,
    edgeInset,
    hasLeadingSlot,
    height,
    isLeadingControlRight,
    isLeadingSlotHidden,
    isTrailingEdgeFree,
    leadingInset,
    trailingInset,
  } = useToolbarGridMetrics();
  const controlInset = leadingControlWidth > 0 ? leadingControlWidth + 4 : 0;
  // On native the shell's top-left slot holds the More menu or the back control, so a row beside
  // it reserves that lane whether or not the page itself has leading actions.
  const reservesLeadingLane = hasLeadingActions || hasLeadingSlot;
  // A hidden slot gives the row its circle and the 4px gap after it. With no page control of its
  // own before the pills, the row then reaches the window's leading edge.
  const isLeadingSlotFreed = reservesLeadingLane && isLeadingSlotHidden && !isLeadingControlRight;
  const isLeadingEdgeFree = isLeadingSlotFreed && controlInset === 0;
  const marginLeft =
    Platform.OS === "web"
      ? reservesLeadingLane
        ? leadingInset + controlInset
        : 0
      : isLeadingEdgeFree
        ? 0
        : (reservesLeadingLane ? leadingInset - (isLeadingSlotFreed ? diameter + 4 : 0) : 0) +
          controlInset;
  const marginRight = Platform.OS === "web" ? 0 : trailingInset;
  // At a free edge the pills still rest the corner controls' 9px gutter from the window edge
  // once scrolled to it, while the viewport (and its fade) runs on to the edge itself. The inline
  // padding wins over the content container's class padding, `px-0` included.
  const freeEdgePadding =
    isLeadingEdgeFree || isTrailingEdgeFree
      ? {
          ...(isLeadingEdgeFree && { paddingLeft: edgeInset }),
          ...(isTrailingEdgeFree && { paddingRight: edgeInset }),
        }
      : undefined;
  const edges = useHorizontalScrollEdges();
  return (
    // The mask takes the viewport's place in the row and dissolves the pills themselves at a side
    // with more to scroll to. It reads the same on an opaque bar, over the space sky, and on a
    // glass bar floating over a page (Learn's map), where a fade into a bar colour cannot work.
    <HorizontalScrollEdgeFadeMask
      className={cn(
        "z-0 min-w-0",
        className,
        // These are viewport invariants, not content padding: keep the native scroll hit area out
        // of the absolute profile trigger's slot and, when present, the More trigger's slot even
        // when the pills do not fill the available row.
        "self-stretch",
        Platform.OS === "web" ? "-my-2" : "flex-none",
      )}
      edges={edges}
      style={[
        style,
        { height: Platform.OS === "web" ? height + 16 : height, marginLeft, marginRight },
      ]}
      testID={props.testID === undefined ? undefined : `${props.testID}-viewport`}
    >
      <ScrollView
        className="flex-1"
        contentContainerClassName={cn(
          "min-h-full flex-row items-center",
          contentContainerClassName,
          Platform.OS === "web" ? "py-2" : "py-2.25",
          Platform.OS === "web" ? undefined : "items-start",
          "gap-2.25",
          // The reserved control lanes already provide the horizontal gaps. Cancel callers'
          // horizontal padding equally so scrolling to either end stops at the last pill edge.
          hasLeadingActions ? MORE_TRIGGER_BALANCED_GUTTER_CLASS_NAME : undefined,
        )}
        horizontal
        showsHorizontalScrollIndicator={false}
        // A horizontal ScrollView still draws a VERTICAL indicator whenever content height exceeds
        // the viewport by even a sub-pixel, and this row pins `height` while its content container
        // is `min-h-full` — so the overflow is routinely 0-1px. The indicator drew on the row's
        // right edge can read as a stray hairline divider beside the menu button.
        showsVerticalScrollIndicator={false}
        {...props}
        contentContainerStyle={[contentContainerStyle, freeEdgePadding]}
        onContentSizeChange={(width, contentHeight) => {
          edges.onContentSizeChange(width, contentHeight);
          onContentSizeChange?.(width, contentHeight);
        }}
        onLayout={(event) => {
          edges.onLayout(event);
          onLayout?.(event);
        }}
        onScroll={(event) => {
          edges.onScroll(event);
          onScroll?.(event);
        }}
        scrollEventThrottle={scrollEventThrottle}
      >
        <View
          className={cn(
            "flex-row",
            "gap-2.25",
            Platform.OS === "web" ? "items-center" : "items-start",
          )}
        >
          {children}
        </View>
      </ScrollView>
    </HorizontalScrollEdgeFadeMask>
  );
}

/** Shared glass pill for page-level view and filter selections. */
export function ToolbarViewButton({
  accessibilityLabel,
  compact = false,
  disabled,
  isDisabled,
  isLoading,
  label,
  onPress,
  pressFeedback = true,
  selected = false,
  sublabel,
  ...props
}: ToolbarViewButtonProps) {
  const { diameter } = useToolbarGridMetrics();
  return (
    <ToolbarGlassSurface
      // UIKit's out-of-bounds glass halo is rectangularly clipped by a scrolling row.
      // Contain the effect at the pill's rounded boundary instead of letting it wash over
      // the neighbouring section header. The native glass material stays inside the pill.
      className="overflow-hidden"
      interactive={pressFeedback && !disabled && !isDisabled && !isLoading}
      selected={selected}
    >
      <Pressable
        accessibilityLabel={accessibilityLabel ?? label}
        accessibilityRole="tab"
        accessibilityState={{ selected }}
        className={cn(
          "min-w-16 items-center justify-center px-4",
          (disabled || isDisabled) && "opacity-50",
        )}
        disabled={disabled}
        onPress={isDisabled || isLoading ? undefined : onPress}
        style={{ height: compact ? Math.max(1, diameter - 8) : diameter }}
        {...props}
      >
        <Text
          className={cn(
            selected
              ? "text-[11px] font-medium text-accent-foreground"
              : "text-[11px] text-muted-foreground",
          )}
        >
          {label}
        </Text>
        {sublabel === undefined ? null : (
          <Text className="text-[9px] leading-[11px] text-muted-foreground/80 tabular-nums">
            {sublabel}
          </Text>
        )}
      </Pressable>
    </ToolbarGlassSurface>
  );
}
